import type { FC, CSSProperties } from "react";
import { PaletteColors } from "$root/styles/themePalette";
import { type SvgProps, Svg } from "@mdotm/shadcn/legacy/components";
import { useUniqueDOMId } from "@mdotm/shadcn/legacy/react-extensions";

export type ColoredRectangleProps = {
	style?: CSSProperties;
	viewBox?: SvgProps["viewBox"];
	className?: string;
	variant?: "vertical" | "horizontal";
} & (
	| {
			color?: string;
	  }
	| {
			texture: string;
			width: number;
			height: number;
	  }
);

const defaultProps = {
	color: PaletteColors.AZURE,
	variant: "horizontal",
	style: {
		color: PaletteColors.AZURE,
		fontSize: 20,
		width: 16,
		height: 4,
	},
	viewBoxVertical: { width: 4, height: 16 },
	viewBoxHorizontal: { width: 16, height: 4 },
};

const ColoredRectangle: FC<ColoredRectangleProps> = ({
	viewBox,
	className,
	variant = defaultProps.variant,
	style = defaultProps.style,
	...rest
}) => {
	const textureId = useUniqueDOMId();
	return (
		<Svg
			viewBox={viewBox ?? (variant === "horizontal" ? defaultProps.viewBoxHorizontal : defaultProps.viewBoxVertical)}
			style={{
				...style,
				...(variant === "horizontal" ? defaultProps.viewBoxHorizontal : defaultProps.viewBoxVertical),
			}}
			className={["shrink-0", className].filter(Boolean).join(" ")}
		>
			{"texture" in rest && (
				<defs>
					<pattern
						id={textureId}
						patternUnits="userSpaceOnUse"
						width={rest.width}
						height={rest.height}
						imageRendering="pixelated"
					>
						<image href={rest.texture} x="0" y="0" width={rest.width} height={rest.height} />
					</pattern>
				</defs>
			)}
			{variant === "horizontal" ? (
				"texture" in rest ? (
					<rect width="16" height="4" fill={`url(#${textureId})`} rx="2" />
				) : (
					<rect y="0" width="16" height="4" rx="2" fill={rest.color ?? defaultProps.color} />
				)
			) : "texture" in rest ? (
				<rect width="4" height="16" fill={`url(#${textureId})`} ry="2" />
			) : (
				<rect x="0" width="4" height="16" ry="2" fill={rest.color ?? defaultProps.color} />
			)}
		</Svg>
	);
};

export default ColoredRectangle;
